import React, { useEffect, useState } from "react";
import api from '../api';
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import viewIcon from "../assets/Images/View.png";
import editIcon from "../assets/Images/Edit.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import usageIcon from "../assets/icons/chatIcon.png";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import { useLocation, useNavigate } from "react-router-dom";
import TopTitleBar from "./TopTitleBar";

const CHAT_API_URL = process.env.REACT_APP_API_URL;

const ChatUsageManagement = () => {
  const [chatUsages, setChatUsages] = useState([]);
  const [chatStats, setChatStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    flagged: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const enterpriseData = location.state?.enterprise;

  useEffect(() => {
    if (!enterpriseData) navigate("/superadmin/conversation-management");
  }, [enterpriseData, navigate]);

  const token =
    localStorage.getItem("access_token") || sessionStorage.getItem("access_token");

  const finalEnterpriseData =
    enterpriseData ||
    location.state?.enterprise ||
    (sessionStorage.getItem("enterprise_id") &&
      sessionStorage.getItem("enterprise_name") && {
        enterprise_id: sessionStorage.getItem("enterprise_id"),
        enterprise_name: sessionStorage.getItem("enterprise_name"),
      });

  const fetchChatUsages = async () => {
    try {
      if (!finalEnterpriseData?.enterprise_id) {
        setChatUsages([]);
        setTotalEntries(0);
        return;
      }

      const res = await api.get(
        `${CHAT_API_URL}/users/${finalEnterpriseData?.enterprise_id}/details`,

        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // console.log("APi ", res.data);

      const responseData = res?.data;
      const rows = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

      const normalizedSearch = searchTerm.trim().toLowerCase();
      // Filter only by searchTerm (user_name), if provided
      const filteredData = rows.filter((item) =>
        (item.user_name?.user_name || "")
          .toLowerCase()
          .includes(normalizedSearch)
      );

      setChatUsages(filteredData);
      setTotalEntries(filteredData.length);
    } catch (err) {
      console.error("Failed to fetch chat usage summary:", err);
      setChatUsages([]);
      setTotalEntries(0);
    }
  };

  useEffect(() => {
    if (finalEnterpriseData?.enterprise_id) {
      sessionStorage.setItem(
        "enterprise_id",
        finalEnterpriseData.enterprise_id
      );
      sessionStorage.setItem(
        "enterprise_name",
        finalEnterpriseData.enterprise_name
      );
    }
  }, [finalEnterpriseData]);

  const fetchStats = async () => {
    try {
      const res = await api.get(`${CHAT_API_URL}/chat/stats/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChatStats(res.data);
    } catch (err) {
      console.error("Failed to fetch chat stats:", err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (!finalEnterpriseData?.enterprise_id) return;
    fetchChatUsages();
  }, [
    finalEnterpriseData?.enterprise_id,
    searchTerm,
    entriesPerPage,
    sortOrder,
    dayFilter,
    currentPage,
  ]);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm("");
    setSortOrder("desc");
    setDayFilter("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    fetchChatUsages();
  };

  const handlePageClick = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`${CHAT_API_URL}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "chat_usage_data.xlsx");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const handleView = (item) => console.log("View", item);
  const handleEdit = (item) => console.log("Edit", item);
  const handleChangeStatus = (item) => console.log("Status change", item);

  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const showingFrom =
    totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const showingTo = Math.min(currentPage * entriesPerPage, totalEntries);

  return (
    <div className="mx-2 pt-1 h-100">
      {/* Stats Boxes */}
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {[
          { label: "Total Messages", value: chatStats?.chat?.total || 0 },
          { label: "Active Messages", value: chatStats?.chat?.active || 0 },
          {
            label: "Completed Messages",
            value: chatStats?.chat?.completed || 0,
          },
        ].map((box, index) => (
          <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between" key={index}>
            <div className="d-flex align-items-center mb-2">
              <img
                src={usageIcon}
                alt={box.label}
                style={{
                  width: "24px",
                  height: "24px",
                }}
                className="icon-image1"
              />
              <h6 className="ms-2 mb-0">{box.label}</h6>
            </div>
            <h2 className="fw-bold">{box.value}</h2>
          </div>
        ))}
      </div>

      {/* Header */}
      {/* <div className="d-flex justify-content-between align-items-center mb-2 mt-1">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          Chat Usage Management Details – {enterpriseData?.enterprise_name}
        </span>
      </div> */}

      <TopTitleBar level="enterprise" enterpriseData={enterpriseData} />

      {/* Filters & Search */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center gap-2">
          <div className="custom-search-wrapper">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Conversations"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button> */}

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button m-0"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolder refresh-prompts">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table conversation-table1 table-sm w-100">
              <thead>
                <tr>
                  <th>User Name</th>
                  <th>Email</th>
                  <th className="text-center">Total Conversations</th>
                  <th className="text-center">Total Messages</th>
                  <th className="text-center">Models Used</th>
                  <th>Uploaded On</th>
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {chatUsages
                  .slice(
                    (currentPage - 1) * entriesPerPage,
                    currentPage * entriesPerPage
                  )
                  .map((item) => (
                    <tr key={item.user_id}>
                      <td>{item.user_name?.user_name || "N/A"}</td>
                      <td>{item.user_name?.user_email || "N/A"}</td>

                      <td className="text-center">
                        <span
                          className="text-primary text-decoration-underline"
                          role="button"
                          onClick={() =>
                            navigate("/superadmin/user-session-management", {
                              state: {
                                user_id: item.user_id,
                                user_name: item.user_name?.user_name || "N/A",
                              },
                            })
                          }
                        >
                          {item.total_conversations}
                        </span>
                      </td>

                      <td className="text-center">
                        <span
                          className="text-primary text-decoration-underline"
                          role="button"
                          onClick={() =>
                            navigate("/superadmin/user-session-management", {
                              state: {
                                user_id: item.user_id,
                                user_name: item.user_name?.user_name || "N/A",
                              },
                            })
                          }
                        >
                          {item.total_messages}
                        </span>
                      </td>

                      <td className="text-center">
                        <span
                          className="text-primary text-decoration-underline"
                          role="button"
                          onClick={() =>
                            navigate("/superadmin/user-session-management", {
                              state: {
                                user_id: item.user_id,
                                user_name: item.user_name?.user_name || "N/A",
                              },
                            })
                          }
                        >
                          {item.total_models_used}
                        </span>
                      </td>

                      <td>{formatDateToUserTimeZone(item.last_updated)}</td>

                      {/* <td>
                        <button
                          className="icon-button"
                          onClick={() => handleView(item)}
                          title="View"
                        >
                          <img src={viewIcon} alt="View" />
                        </button>
                        <button
                          className="icon-button"
                          onClick={() => handleEdit(item)}
                          title="Edit"
                        >
                          <img src={editIcon} alt="Edit" />
                        </button>
                        <button
                          className="icon-button"
                          onClick={() => handleChangeStatus(item)}
                          title="Change Status"
                        >
                          <img src={changeStatusIcon} alt="Change Status" />
                        </button>
                      </td> */}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex fixed-bottom-footer justify-content-between align-items-center mt-2 table-footer">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatUsageManagement;
